import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/ui/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/components/ui/card";
import { Copy, CheckCheck, Database } from "lucide-react";
import { toast } from "react-hot-toast";
import type { CacheDebugEntry } from "@/machines/api/requests/events.requests";

interface ErrorDetailModalProps {
  payload: {
    title?: string;
    error: any;
    requestPayload?: any;
    cacheDebugInfo?: CacheDebugEntry[];
  };
  onClose: () => void;
}

const ErrorDetailModal: React.FC<ErrorDetailModalProps> = ({ payload, onClose }) => {
  const { t } = useTranslation("machines");
  const { title = "Error Details", error, requestPayload, cacheDebugInfo } = payload;
  const [copied, setCopied] = React.useState(false);
  const [copiedRequest, setCopiedRequest] = React.useState(false);
  const [copiedCache, setCopiedCache] = React.useState(false);

  // Format error for display
  const errorDetails = React.useMemo(() => {
    try {
      // If error is already an object, use it directly
      if (typeof error === 'object' && error !== null) {
        return JSON.stringify(error, null, 2);
      }
      // If error is a string, try to parse it
      if (typeof error === 'string') {
        try {
          const parsed = JSON.parse(error);
          return JSON.stringify(parsed, null, 2);
        } catch {
          // If not JSON, return as is
          return error;
        }
      }
      // Fallback
      return String(error);
    } catch {
      return String(error);
    }
  }, [error]);

  // Format request payload for display
  const requestDetails = React.useMemo(() => {
    if (!requestPayload) return null;
    try {
      if (typeof requestPayload === 'object' && requestPayload !== null) {
        return JSON.stringify(requestPayload, null, 2);
      }
      if (typeof requestPayload === 'string') {
        try {
          const parsed = JSON.parse(requestPayload);
          return JSON.stringify(parsed, null, 2);
        } catch {
          return requestPayload;
        }
      }
      return String(requestPayload);
    } catch {
      return String(requestPayload);
    }
  }, [requestPayload]);

  // Extract main error message
  const errorMessage = React.useMemo(() => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.message) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return t("errorDetail.unknownError");
  }, [error, t]);

  // Extract status code
  const statusCode = React.useMemo(() => {
    if (error?.response?.status) {
      return error.response.status;
    }
    if (error?.status) {
      return error.status;
    }
    return null;
  }, [error]);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(errorDetails);
      setCopied(true);
      toast.success(t("errorDetail.toast.errorCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(t("errorDetail.toast.copyFailed"));
    }
  };

  const handleCopyRequestToClipboard = async () => {
    if (!requestDetails) return;
    try {
      await navigator.clipboard.writeText(requestDetails);
      setCopiedRequest(true);
      toast.success(t("errorDetail.toast.requestCopied"));
      setTimeout(() => setCopiedRequest(false), 2000);
    } catch (err) {
      toast.error(t("errorDetail.toast.copyFailed"));
    }
  };

  const handleCopyCacheToClipboard = async () => {
    if (!cacheDebugInfo) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(cacheDebugInfo, null, 2));
      setCopiedCache(true);
      toast.success(t("errorDetail.toast.cacheCopied"));
      setTimeout(() => setCopiedCache(false), 2000);
    } catch (err) {
      toast.error(t("errorDetail.toast.copyFailed"));
    }
  };

  return (
    <div className="space-y-4">
      {/* Request Payload Card - show if available */}
      {requestDetails && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{t("errorDetail.requestTitle")}</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyRequestToClipboard}
                className="gap-2"
              >
                {copiedRequest ? (
                  <>
                    <CheckCheck className="h-4 w-4" />
                    {t("errorDetail.copied")}
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    {t("errorDetail.copyJson")}
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-4 rounded-md overflow-auto max-h-96 font-mono">
              {requestDetails}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Cache Resolution Card - shows which timestamps were resolved from cache vs fallback */}
      {cacheDebugInfo && cacheDebugInfo.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="h-4 w-4" />
                {t("errorDetail.cacheTitle")}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyCacheToClipboard}
                className="gap-2"
              >
                {copiedCache ? (
                  <>
                    <CheckCheck className="h-4 w-4" />
                    {t("errorDetail.copied")}
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    {t("errorDetail.copyJson")}
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {cacheDebugInfo.map((entry) => (
                <div key={entry.commandIndex} className="text-xs border rounded-md p-3 space-y-1">
                  <div className="flex items-center gap-2 font-medium">
                    <span>{t("errorDetail.command", { index: entry.commandIndex })}</span>
                    <span className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono uppercase">{entry.commandType}</span>
                    <span className="text-muted-foreground font-mono">{entry.id}</span>
                  </div>
                  <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 font-mono">
                    <span className="text-muted-foreground">start_time:</span>
                    <span>
                      {entry.start_time.resolved}
                      {' '}
                      <span className={`px-1 py-0.5 rounded text-[10px] ${entry.start_time.source === 'cache' ? 'bg-green-500/20 text-green-700 dark:text-green-400' : 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400'}`}>
                        {entry.start_time.source}
                      </span>
                      {entry.start_time.eventLayer && (
                        <span className={`ml-1 px-1 py-0.5 rounded text-[10px] ${entry.start_time.eventLayer.includes('truck') ? 'bg-red-500/20 text-red-700 dark:text-red-400' : 'bg-blue-500/20 text-blue-700 dark:text-blue-400'}`}>
                          {entry.start_time.eventLayer}
                        </span>
                      )}
                    </span>
                    <span className="text-muted-foreground">end_time:</span>
                    <span>
                      {entry.end_time.resolved}
                      {' '}
                      <span className={`px-1 py-0.5 rounded text-[10px] ${entry.end_time.source === 'cache' ? 'bg-green-500/20 text-green-700 dark:text-green-400' : 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400'}`}>
                        {entry.end_time.source}
                      </span>
                      {entry.end_time.eventLayer && (
                        <span className={`ml-1 px-1 py-0.5 rounded text-[10px] ${entry.end_time.eventLayer.includes('truck') ? 'bg-red-500/20 text-red-700 dark:text-red-400' : 'bg-blue-500/20 text-blue-700 dark:text-blue-400'}`}>
                          {entry.end_time.eventLayer}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Response Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">{t("errorDetail.responseTitle")}</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyToClipboard}
              className="gap-2"
            >
              {copied ? (
                <>
                  <CheckCheck className="h-4 w-4" />
                  {t("errorDetail.copied")}
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  {t("errorDetail.copyJson")}
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <pre className="text-xs bg-muted p-4 rounded-md overflow-auto max-h-96 font-mono">
            {errorDetails}
          </pre>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button variant="default" onClick={onClose}>
          {t("actions.close")}
        </Button>
      </div>
    </div>
  );
};

export default ErrorDetailModal;
